import React, { useEffect } from "react";
import { X, Plus, Pencil, Trash2 } from "lucide-react";

export const PageTitle = ({ title, subtitle, action }) => (
  <div className="mb-7 flex items-end justify-between gap-4">
    <div>
      <h1 className="font-display text-3xl text-[#16321f]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-[#6b7268]">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-2xl border border-[#e4e6dd] bg-white p-6 ${className}`} {...rest}>
    {children}
  </div>
);

export const StatCard = ({ label, value, sub, accent = "#14512f", testId }) => (
  <div className="rounded-2xl border border-[#e4e6dd] bg-white p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-[#8a8f83]">{label}</div>
    <div className="mt-2 font-display text-3xl" style={{ color: accent }}>{value}</div>
    {sub && <div className="mt-1 text-xs text-[#8a8f83]">{sub}</div>}
  </div>
);

export const Btn = ({ children, variant = "primary", className = "", ...rest }) => {
  const styles = {
    primary: "bg-[#14512f] text-white hover:bg-[#0f4526]",
    outline: "border border-[#dcdfd4] bg-white text-[#3a423b] hover:bg-[#f0f2ea]",
    ghost: "text-[#6b7268] hover:bg-[#f0f2ea]",
    danger: "border border-[#e7cdc9] bg-white text-[#b4342a] hover:bg-[#fbeceb]",
  }[variant];
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${styles} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export const AddButton = ({ label, onClick }) => (
  <Btn onClick={onClick} data-testid="add-button"><Plus className="h-4 w-4" />{label}</Btn>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#8a8f83]">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-lg border border-[#dcdfd4] bg-[#fbfcf8] px-3 py-2 text-sm text-[#1b2420] outline-none transition-colors placeholder:text-[#a7ada0] focus:border-[#14512f]";

export const TextInput = (props) => <input className={inputCls} {...props} />;
export const TextArea = (props) => <textarea rows={3} className={`${inputCls} resize-none`} {...props} />;
export const Select = ({ children, ...props }) => (
  <select className={inputCls} {...props}>{children}</select>
);

export const EmptyState = ({ text }) => (
  <div className="grid place-items-center rounded-xl border border-dashed border-[#d7dacd] py-14 text-sm text-[#8a8f83]" data-testid="empty-state">
    {text}
  </div>
);

export const RowActions = ({ onEdit, onDelete }) => (
  <div className="flex justify-end gap-1">
    <button onClick={onEdit} data-testid="row-edit" className="grid h-8 w-8 place-items-center rounded-md text-[#6b7268] hover:bg-[#eef1e9]">
      <Pencil className="h-4 w-4" />
    </button>
    <button onClick={onDelete} data-testid="row-delete" className="grid h-8 w-8 place-items-center rounded-md text-[#b4342a] hover:bg-[#fbeceb]">
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export const Modal = ({ open, onClose, title, children, footer }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-[#e4e6dd] bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-xl text-[#16321f]">{title}</h3>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-md text-[#6b7268] hover:bg-[#f0f2ea]" data-testid="modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
};

export const Table = ({ head, children }) => (
  <div className="overflow-hidden rounded-xl border border-[#e4e6dd]">
    <table className="w-full text-sm">
      <thead className="bg-[#f4f6ee] text-left text-xs uppercase tracking-wider text-[#8a8f83]">
        <tr>{head.map((h, i) => <th key={i} className={`px-4 py-3 font-medium ${h.right ? "text-right" : ""}`}>{h.label}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-[#eef0e8]">{children}</tbody>
    </table>
  </div>
);
